/**
 * Lissajous Visualizer - 2D phase correlation curves
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class LissajousVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'lissajous',
    name: 'Lissajous Curves',
    description: '2D phase correlation patterns',
    category: '2d',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, activeWaves, speed, time, colorMode = 'wave', uploadedAudioData, isUploadedAudioPlaying } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Clear canvas
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    // Draw axes
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx, height);
    ctx.moveTo(0, cy);
    ctx.lineTo(width, cy);
    ctx.stroke();

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      // Limit to top 3-4 strongest frequencies to reduce visual noise
      const sorted = parsedData.sort((a, b) => b.amp - a.amp).slice(0, 2);
      displayWaves = sorted.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) return;

    const margin = 40;
    const radius = (Math.min(width, height) / 2) - margin;
    const segments = 1000;
    const history = 2.0;

    // Separate waves by channel
    const pureLeftWaves = displayWaves.filter(w => w.channel === 'left');
    const pureRightWaves = displayWaves.filter(w => w.channel === 'right');
    const bothWaves = displayWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
    const autoPanWaves = displayWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);
    const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

    if (colorMode === 'channel' && (hasDistinctChannels || autoPanWaves.length > 0)) {
      // CHANNEL COLOR MODE
      if (pureLeftWaves.length > 0) {
        this.drawLissajousTrace(ctx, pureLeftWaves, '#3b82f6', segments, history, time, speed, cx, cy, radius, isUploadedAudioPlaying);
      }
      if (pureRightWaves.length > 0) {
        this.drawLissajousTrace(ctx, pureRightWaves, '#ec4899', segments, history, time, speed, cx, cy, radius, isUploadedAudioPlaying);
      }
      if (bothWaves.length > 0) {
        this.drawLissajousTrace(ctx, bothWaves, '#10B981', segments, history, time, speed, cx, cy, radius, isUploadedAudioPlaying);
      }
      if (autoPanWaves.length > 0) {
        const panColor = this.getPanColor(0); // Simplified
        this.drawLissajousTrace(ctx, autoPanWaves, panColor, segments, history, time, speed, cx, cy, radius, isUploadedAudioPlaying);
      }

      // Channel labels
      if (hasDistinctChannels) {
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.font = '11px sans-serif';
        if (pureLeftWaves.length > 0) ctx.fillText('L', 8, cy - 10);
        if (pureRightWaves.length > 0) ctx.fillText('R', 8, cy + 15);
      }
    } else {
      // WAVE COLOR MODE - use individual wave colors
      this.drawLissajousTraceWithWaveColors(ctx, displayWaves, segments, history, time, speed, cx, cy, radius, isUploadedAudioPlaying);
    }

    ctx.shadowBlur = 0;
  }

  private drawLissajousTraceWithWaveColors(
    ctx: CanvasRenderingContext2D,
    waves: any[],
    segments: number,
    history: number,
    time: number,
    speed: number,
    cx: number,
    cy: number,
    radius: number,
    isMusic?: boolean
  ): void {
    if (waves.length === 0) return;

    // In wave color mode, draw each pair of waves independently
    // Use alternating waves: odd-indexed waves as X, even-indexed as Y
    for (let i = 0; i < waves.length; i++) {
      const color = this.getWaveColor(waves[i], 'wave');
      const xWave = waves[i];
      const yWave = waves[(i + 1) % waves.length]; // Pair with next wave, wrap around
      
      this.drawLissajousTracePair(
        ctx, 
        xWave, 
        yWave, 
        color, 
        segments, 
        history, 
        time, 
        speed, 
        cx, 
        cy, 
        radius,
        isMusic
      );
    }
  }

  private drawLissajousTrace(
    ctx: CanvasRenderingContext2D,
    waves: any[],
    color: string,
    segments: number,
    history: number,
    time: number,
    speed: number,
    cx: number,
    cy: number,
    radius: number,
    isMusic?: boolean
  ): void {
    if (waves.length === 0) return;

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowBlur = isMusic ? 0 : 8;
    ctx.shadowColor = color;

    let prevX = 0, prevY = 0;

    for (let i = 0; i < segments; i++) {
      const t = time - (history * (1 - (i / segments)));
      const { x, y } = this.computeLissajous(waves, t, speed);

      const plotX = cx + (x * radius);
      const plotY = cy - (y * radius);

      if (i === 0) {
        ctx.moveTo(plotX, plotY);
      } else if (isMusic && i > 1) {
        const dist = Math.sqrt((plotX - prevX) ** 2 + (plotY - prevY) ** 2);
        if (dist < 50) {
          const cpX = (prevX + plotX) / 2;
          const cpY = (prevY + plotY) / 2;
          ctx.quadraticCurveTo(prevX, prevY, cpX, cpY);
        } else {
          ctx.lineTo(plotX, plotY);
        }
      } else {
        ctx.lineTo(plotX, plotY);
      }

      prevX = plotX;
      prevY = plotY;
    }
    ctx.stroke();
  }

  private drawLissajousTracePair(
    ctx: CanvasRenderingContext2D,
    xWave: any,
    yWave: any,
    color: string,
    segments: number,
    history: number,
    time: number,
    speed: number,
    cx: number,
    cy: number,
    radius: number,
    isMusic?: boolean
  ): void {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowBlur = isMusic ? 0 : 8;
    ctx.shadowColor = color;

    let prevX = 0, prevY = 0;

    for (let i = 0; i < segments; i++) {
      const t = time - (history * (1 - (i / segments)));
      const { x, y } = this.computeLissajousPair(xWave, yWave, t, speed);

      const plotX = cx + (x * radius);
      const plotY = cy - (y * radius);

      if (i === 0) {
        ctx.moveTo(plotX, plotY);
      } else if (isMusic && i > 1) {
        const dist = Math.sqrt((plotX - prevX) ** 2 + (plotY - prevY) ** 2);
        if (dist < 50) {
          const cpX = (prevX + plotX) / 2;
          const cpY = (prevY + plotY) / 2;
          ctx.quadraticCurveTo(prevX, prevY, cpX, cpY);
        } else {
          ctx.lineTo(plotX, plotY);
        }
      } else {
        ctx.lineTo(plotX, plotY);
      }

      prevX = plotX;
      prevY = plotY;
    }
    ctx.stroke();
  }

  private computeLissajous(waves: any[], t: number, speed: number): { x: number; y: number } {
    if (waves.length === 0) return { x: 0, y: 0 };

    const xWave = waves[0];
    const yWaves = waves.slice(1);

    const xPhase = (xWave.phase * Math.PI) / 180;
    const effectiveFreqX = xWave.freq * speed;
    const xVal = Math.sin(2 * Math.PI * effectiveFreqX * t + xPhase);

    let yVal = 0;
    if (yWaves.length > 0) {
      let yMaxAmp = yWaves.reduce((s: number, w: any) => s + w.amp, 0);
      yMaxAmp = yMaxAmp === 0 ? 1 : yMaxAmp;
      yWaves.forEach((w: any) => {
        const wPhase = (w.phase * Math.PI) / 180;
        const effectiveFreq = w.freq * speed;
        yVal += (w.amp) * Math.sin(2 * Math.PI * effectiveFreq * t + wPhase);
      });
      yVal = yVal / yMaxAmp;
    }

    return { x: xVal, y: yVal };
  }

  private computeLissajousPair(xWave: any, yWave: any, t: number, speed: number): { x: number; y: number } {
    const xPhase = (xWave.phase * Math.PI) / 180;
    const effectiveFreqX = xWave.freq * speed;
    const xVal = Math.sin(2 * Math.PI * effectiveFreqX * t + xPhase);

    const yPhase = (yWave.phase * Math.PI) / 180;
    const effectiveFreqY = yWave.freq * speed;
    const yVal = Math.sin(2 * Math.PI * effectiveFreqY * t + yPhase);

    return { x: xVal, y: yVal };
  }
}
